import {
  HubSpotInitQuoteRequest,
  HubSpotInitQuoteResponse,
  HubspotLinkedAccountsResponse,
  OpportunityResponseSchema,
  OpportunitySearchResponse,
  OpportunitySearchResponseItem,
} from '@monetize/types/app';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from './axios';

export const crmServiceQueryKeys = {
  base: ['crm'] as const,
  opportunities: () => [...crmServiceQueryKeys.base, 'opportunities'] as const,
  hubspotLinkedAccounts: (opportunityId: string) =>
    [
      ...crmServiceQueryKeys.base,
      'hubspotLinkedAccounts',
      opportunityId,
    ] as const,
};

// link an opportunity id to a quote. customId = opportunityId
export async function doLinkOpportunityToQuote(
  quoteId: string,
  customId: string,
): Promise<OpportunityResponseSchema> {
  const res = await apiPut<OpportunityResponseSchema>(
    `/api/opportunities/customId/${customId}/quotes/${quoteId}/link`,
  );
  return res.data;
}

/**
 * Search for opportunities in either Hubspot or Salesforce
 *
 * @param accountId
 * @param searchTerm
 * @param options
 * @returns
 */
export function useSearchCrmOpportunities<
  SelectData = OpportunitySearchResponseItem[],
>(
  accountId: string,
  searchTerm: string,
  options: Partial<
    UseQueryOptions<OpportunitySearchResponseItem[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...crmServiceQueryKeys.opportunities(), accountId, searchTerm],
    queryFn: () => doSearchCrmOpportunities(accountId, searchTerm),
    placeholderData: (previousData, previousQuery) => previousData,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function doSearchCrmOpportunities(
  accountId: string,
  searchTerm: string,
) {
  return apiGet<OpportunitySearchResponse>(
    `/connector/crm/salesforce/search/accounts/${accountId}/opportunities`,
    { params: { q: searchTerm } },
  ).then((res) => res.data.results);
}

/**
 * After creating a new MN account from a HubSpot company, associate it back so
 * future calls to the linkedAccounts API return the MN id.
 */
export async function doAssociateHubspotCompanyWithAccount(
  hubspotCompanyId: string,
  accountId: string,
): Promise<void> {
  await apiPut(
    `/api/accounts/${accountId}/hubspot/companies/${hubspotCompanyId}`,
  );
}

/**
 * After creating a new MN contact from a HubSpot contact, associate it back so
 * future calls to the linkedAccounts API return the MN id.
 */
export async function doAssociateHubspotContactWithContact(
  hubspotContactId: string,
  contactId: string,
): Promise<void> {
  await apiPut(
    `/api/contacts/${contactId}/hubspot/contacts/${hubspotContactId}`,
  );
}

export function useGetHubspotLinkedAccounts<
  SelectData = HubspotLinkedAccountsResponse,
>(
  opportunityId: string,
  options: Partial<
    UseQueryOptions<HubspotLinkedAccountsResponse, unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: crmServiceQueryKeys.hubspotLinkedAccounts(opportunityId),
    queryFn: () =>
      apiGet<HubspotLinkedAccountsResponse>(
        `/api/opportunities/${opportunityId}/hubspot/linkedAccounts`,
      ).then((res) => res.data),
    enabled: !!opportunityId,
    refetchOnWindowFocus: false,
    ...options,
  });
}

export function useInitHubspotQuote(
  options: Partial<
    UseMutationOptions<
      HubSpotInitQuoteResponse,
      unknown,
      HubSpotInitQuoteRequest
    >
  > = {},
) {
  return useMutation<
    HubSpotInitQuoteResponse,
    unknown,
    HubSpotInitQuoteRequest
  >({
    mutationFn: (payload) =>
      apiPost<HubSpotInitQuoteResponse>(
        `/connector/crm/hubspot/connect/deal/init`,
        payload,
      ).then((res) => res.data),
    ...options,
  });
}
